import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { HttpClient } from "@angular/common/http";
import { firstValueFrom } from "rxjs";
import { SchoolAndStaffDetail } from "./staff-login.service";
import { SchoolAndStaffInfo, StaffClassDetail } from "./staff-class.service";
import { commonFunctionService } from "../../utils/commonFunctions";
import { AppSettings } from "../../../config/config";
import { apiConstants } from "../../constant/apiConstant";

const STAFF_DETAILS_KEY = 'staffDetails';
const SCHOOL_AND_STAFF_INFO_KEY = 'schoolAndStaffInfo';
const CLASS_DATA_KEY = 'CLASS_DATA';
const FEATURES_LIST_KEY = 'FeaturesList';
const EXTENSION_SERVICES_KEY = 'extensionServices';
const ANY_UNREAD_BROADCAST_KEY = 'anyUnreadBroadcast';

export interface FeatureFlagEntry {
  BluewingsFeatureName: string;
  ActiveStatus: 'ENABLED' | 'DISABLED';
}

export interface ExtensionServiceEntry {
  serviceName: string;
  serviceStatus: 'ENABLED' | 'DISABLED';
}

@Injectable({ providedIn: 'root' })
export class StaffSessionService {
  private url = AppSettings.API_ENDPOINT_STAFF;
  private staffLogoutEndpoint = apiConstants.staffLogout;

  constructor(private router: Router, private commonFunction: commonFunctionService, private http: HttpClient) {}

  getStaffDetails(): SchoolAndStaffDetail | null {
    const raw = localStorage.getItem(STAFF_DETAILS_KEY);
    if (!raw) { return null; }
    try {
      return JSON.parse(raw) as SchoolAndStaffDetail;
    } catch {
      return null;
    }
  }

  // Persists the richer school/staff profile returned by the class-list API.
  // Called on every successful getClassDetails response so it stays current.
  setSchoolAndStaffInfo(info: SchoolAndStaffInfo) {
    localStorage.setItem(SCHOOL_AND_STAFF_INFO_KEY, JSON.stringify(info));
  }

  getSchoolAndStaffInfo(): SchoolAndStaffInfo | null {
    const raw = localStorage.getItem(SCHOOL_AND_STAFF_INFO_KEY);
    if (!raw) { return null; }
    try {
      return JSON.parse(raw) as SchoolAndStaffInfo;
    } catch {
      return null;
    }
  }

  // Cache of the class card tapped on the dashboard — read back by the
  // class-view page on mount, mirroring the mobile app's CLASS_DATA usage.
  setClassData(classData: StaffClassDetail) {
    localStorage.setItem(CLASS_DATA_KEY, JSON.stringify(classData));
  }

  getClassData(): StaffClassDetail | null {
    const raw = localStorage.getItem(CLASS_DATA_KEY);
    if (!raw) { return null; }
    try {
      return JSON.parse(raw) as StaffClassDetail;
    } catch {
      return null;
    }
  }

  // Cache of the school's feature-flag list, written whenever the Dashboard
  // loads (getClassDetails response), and read by any screen that needs to
  // gate a tile/section — e.g. Class Overview, which never fetches this
  // itself and is only ever as fresh as Dashboard's last load.
  setFeatureList(features: FeatureFlagEntry[]) {
    localStorage.setItem(FEATURES_LIST_KEY, JSON.stringify(features || []));
  }

  getFeatureList(): FeatureFlagEntry[] {
    const raw = localStorage.getItem(FEATURES_LIST_KEY);
    if (!raw) { return []; }
    try {
      return JSON.parse(raw) as FeatureFlagEntry[];
    } catch {
      return [];
    }
  }

  // Every feature defaults to hidden until an ENABLED entry is found —
  // except BROADCAST_VIEW, which defaults to visible (matches the mobile
  // app's useState(true) default: a slow/failed features load still shows
  // the broadcast UI while every other feature stays hidden).
  isFeatureEnabled(featureName: string): boolean {
    const entry = this.getFeatureList().find(f => f.BluewingsFeatureName === featureName);
    if (!entry) { return featureName === 'BROADCAST_VIEW'; }
    return entry.ActiveStatus !== 'DISABLED';
  }

  // Strict variant for features whose mobile-app gate checks
  // ActiveStatus === 'ENABLED' exactly (e.g. Leave Management's Drawer.js:
  // 110-114) rather than "anything but DISABLED" — defaults to hidden both
  // when the entry is missing and when its status is anything other than
  // the literal 'ENABLED' string.
  isFeatureExplicitlyEnabled(featureName: string): boolean {
    const entry = this.getFeatureList().find(f => f.BluewingsFeatureName === featureName);
    return entry?.ActiveStatus === 'ENABLED';
  }

  // Extension services (Academic Calendar, Hourhive, Doorway, Trackify,
  // Library Management/Nexus, ...) are school-level, set at login time as
  // the 'extensionServices' cookie (see new-login-with-domain-verification
  // component's staffLogin()) and refreshed on every dashboard load from
  // getClassDetails' schoolExtensionServicePermissions — same source/shape
  // the admin web app's menu.component.ts reads via checkServiceStatus.
  // isAnyUnreadBroadcast comes back at the top level of the getClassDetails
  // ("my classes") response, refreshed on every dashboard load — cached here
  // so the layout's broadcast icon can show/hide its unread dot without a
  // separate call.
  setAnyUnreadBroadcast(isUnread: boolean) {
    localStorage.setItem(ANY_UNREAD_BROADCAST_KEY, JSON.stringify(!!isUnread));
  }

  getAnyUnreadBroadcast(): boolean {
    return localStorage.getItem(ANY_UNREAD_BROADCAST_KEY) === 'true';
  }

  setExtensionServices(services: ExtensionServiceEntry[] | null) {
    localStorage.setItem(EXTENSION_SERVICES_KEY, JSON.stringify(services || []));
  }

  getExtensionServices(): ExtensionServiceEntry[] {
    let raw = localStorage.getItem(EXTENSION_SERVICES_KEY);
    if (raw === null) {
      // First render right after login: localStorage hasn't been backfilled
      // yet, fall back to the cookie written synchronously at login time.
      const cookie = document.cookie.split(';').find(c => c.trim().startsWith(EXTENSION_SERVICES_KEY + '='));
      const cookieValue = cookie ? decodeURIComponent(cookie.split('=')[1]) : null;
      if (cookieValue) {
        localStorage.setItem(EXTENSION_SERVICES_KEY, cookieValue);
        raw = cookieValue;
      }
    }
    if (!raw) { return []; }
    try {
      return JSON.parse(raw) as ExtensionServiceEntry[];
    } catch {
      return [];
    }
  }

  isExtensionServiceEnabled(serviceName: string): boolean {
    const entry = this.getExtensionServices().find(s => s.serviceName === serviceName);
    return !!entry && entry.serviceStatus === 'ENABLED';
  }

  getInitials(name: string): string {
    if (!name) { return ''; }
    const parts = name.trim().split(/\s+/);
    return parts.slice(0, 2).map(p => p.charAt(0).toUpperCase()).join('');
  }

  async logout() {
    try {
      await firstValueFrom(this.http.put(`${this.url}${this.staffLogoutEndpoint}`, {}));
    } catch {
      // Clear local session regardless — an unreachable/failed logout call
      // must never trap the user in a logged-in state on the client.
    }
    // Preserve the chosen display language across logout — it's a device/
    // browser preference, not part of the session, and localStorage.clear()
    // would otherwise silently reset it back to English on the login screen.
    const savedLang = localStorage.getItem('lang');
    localStorage.clear();
    if (savedLang) {
      localStorage.setItem('lang', savedLang);
    }
    const hostName = window.location.hostname;
    const urlHost = hostName.split('.')[1];
    if (urlHost === 'localhost') {
      this.commonFunction.deleteAllLocalCookies();
    } else {
      this.commonFunction.deleteAllCookies();
    }
    this.router.navigate(['/login']);
  }
}
